'use client'

// Opted out of React Compiler with the rest of the grid (see data-grid.tsx).
'use no memo'

// Ours, listed for the next sync with ReUI (definitions also carry an `Ours:` comment):
// - The pinned placeholder cell sticks by `stickySides` (`getDataGridStickySide`,
//   `getDataGridCanStick`, `getDataGridStickyEdge`), and headers render through
//   `renderDataGridTableHeadCellContent`.
// - Props `header` (which skips building the built-in head), `rows`, `scrollElementRef` and
//   `tableRef`, and the optional virtualizer defaults in `DataGridTableVirtualizerOptions`.
// - The fetch-more latch also re-arms when the first or last row changes, and while `rows`
//   replace the body.
// - Rows keep their measured heights through a column resize drag and are measured once after
//   it; under the column window each holds the tallest height it has shown (`rowHeight`).
// - The column window reads its scroll offset ahead of the rows (`observeColumnOffset`).

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode, Ref, RefObject } from 'react'
import { useDataGrid, useDataGridAtom } from './data-grid'
import type { DataGridFeatures, DataGridTableInstance } from './data-grid'
import {
  getDataGridCanStick,
  getDataGridStickyEdge,
  getDataGridStickySide,
} from './data-grid-column-layout'
import {
  DataGridTableAddRow,
  DataGridTableBase,
  DataGridTableBody,
  DataGridTableEmpty,
  DataGridTableFillBodyCell,
  DataGridTableFillHeadCell,
  DataGridTableFoot,
  DataGridTableHead,
  DataGridTableHeadRow,
  DataGridTableHeadRowCell,
  DataGridTableHeadRowCellResize,
  DataGridTableRenderedRow,
  DataGridTableRowSpacer,
  DataGridTableViewport,
  getDataGridHeadCellResizeHeader,
  getDataGridScrollAreaViewport,
  getDataGridTableMergedHeaderGroups,
  getDataGridTableRowSections,
  getPinningStyles,
  hasDataGridTableRightPinnedColumns,
  renderDataGridTableHeadCellContent,
} from './data-grid-table'
import type { Column, Row } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { VirtualItem, Virtualizer, VirtualizerOptions } from '@tanstack/react-virtual'
import { cn } from '@repo/ui/lib/utils'

import { Spinner } from '@repo/ui/components/ui/spinner'

type DataGridTableColumnScrollRequest = {
  align: DataGridTableVirtualScrollAlignment
  behavior: ScrollBehavior
  columnId: string | undefined
  columnIndex: number
  scrollElement: HTMLElement
}

interface DataGridTableColumnVirtualizerOptions {
  /** Off unless explicitly true; a column window is opt-in. */
  enabled?: boolean
  overscan?: number
}

// Ours: the column window reads `scrollLeft` before the row virtualizer re-renders the rows in
// the same scroll event. Read after them, it lays the whole table out again on every vertical
// scroll. A capture listener on the scroll element runs before its other listeners. Otherwise it
// matches TanStack's `observeElementOffset`, re-reading the offset once the scroll settles.
const observeColumnOffset: VirtualizerOptions<HTMLElement, Element>['observeElementOffset'] = (
  instance,
  report,
) => {
  const element = instance.scrollElement
  const targetWindow = instance.targetWindow
  if (!element || !targetWindow) return
  const readOffset = () => element.scrollLeft * (instance.options.isRtl ? -1 : 1)
  let settleTimer: number | undefined
  const onScroll = () => {
    const offset = readOffset()
    targetWindow.clearTimeout(settleTimer)
    settleTimer = targetWindow.setTimeout(
      () => report(readOffset(), false),
      instance.options.isScrollingResetDelay,
    )
    report(offset, true)
  }
  element.addEventListener('scroll', onScroll, { capture: true, passive: true })
  return () => {
    element.removeEventListener('scroll', onScroll, { capture: true })
    targetWindow.clearTimeout(settleTimer)
  }
}

type DataGridTableVirtualizerInstance = Virtualizer<HTMLElement, HTMLTableRowElement>

// Ours: `observeElementOffset`, `observeElementRect` and `scrollToFn` are optional, as
// `useVirtualizer` defaults them.
type DataGridTableVirtualizerOptions<TData extends object> = Omit<
  VirtualizerOptions<HTMLElement, HTMLTableRowElement>,
  | 'count'
  | 'estimateSize'
  | 'getItemKey'
  | 'getScrollElement'
  | 'observeElementOffset'
  | 'observeElementRect'
  | 'scrollToFn'
> &
  Partial<
    Pick<
      VirtualizerOptions<HTMLElement, HTMLTableRowElement>,
      'observeElementOffset' | 'observeElementRect' | 'scrollToFn'
    >
  > & {
    estimateSize?: (index: number, row: Row<DataGridFeatures, TData>) => number
    getItemKey?: (index: number, row: Row<DataGridFeatures, TData>) => number | string
    getScrollElement?: (elements: DataGridTableVirtualScrollElements) => HTMLElement | null
  }

interface DataGridTableVirtualProps<TData extends object> {
  /**
   * Opt-in horizontal virtualization of the CENTER columns. Activates only
   * under a fixed table layout with a single ungrouped header row; pinned
   * columns stay mounted, and anything else falls back to full-column
   * rendering. `scrollBehavior` is shared with controlled row scrolling.
   */
  columnVirtualizerOptions?: DataGridTableColumnVirtualizerOptions
  estimateSize?: number
  fetchMoreOffset?: number
  footerContent?: ReactNode
  hasMore?: boolean
  /** Ours: replaces the built-in head, e.g. with the adapter's `TableHeader`; no spacer follows it. */
  header?: ReactNode
  height?: number | string
  isFetchingMore?: boolean
  onFetchMore?: () => void
  overscan?: number
  renderHeader?: boolean
  /** Ours: rendered in place of the data rows, e.g. skeleton rows while new rows load. */
  rows?: ReactNode
  /** Scroll animation used when revealing a controlled target row. */
  scrollBehavior?: ScrollBehavior
  /**
   * Ours: an ancestor that owns scrolling. The grid then renders no overflow container of its own,
   * so the sticky header, pinned columns and the virtualizer all follow this element.
   */
  scrollElementRef?: RefObject<HTMLElement | null>
  /** Alignment used when revealing a controlled target column. */
  scrollToColumnAlign?: DataGridTableVirtualScrollAlignment
  /** Index within the center (non-pinned) visible leaf columns to reveal. */
  scrollToColumnIndex?: number
  /** Alignment used when revealing a controlled target row. Defaults to auto. */
  scrollToRowAlign?: DataGridTableVirtualScrollAlignment
  /** Index within the center (non-pinned) row section to reveal. */
  scrollToRowIndex?: number
  /** Ours: the `<table>` element, for callers that scroll a column into view. */
  tableRef?: Ref<HTMLTableElement>
  virtualizerOptions?: DataGridTableVirtualizerOptions<TData>
}

type DataGridTableVirtualScrollAlignment = 'auto' | 'center' | 'end' | 'start'

type DataGridTableVirtualScrollElements = {
  containerElement: HTMLDivElement | null
  scrollElement: HTMLElement | null
}

type DataGridTableVirtualScrollRequest = {
  align: DataGridTableVirtualScrollAlignment
  behavior: ScrollBehavior
  containerElement: HTMLDivElement
  headerSticky: boolean
  isVirtualizationEnabled: boolean
  rowId: string | undefined
  rowIndex: number
  scrollElement: HTMLElement
}

interface VirtualBodyProps<TData extends object> {
  allRowsLoadedMessage: ReactNode
  bottomRows: Row<DataGridFeatures, TData>[]
  centerColumnWindow?: { end: number; start: number }
  centerRows: Row<DataGridFeatures, TData>[]
  hasMore?: boolean
  isFetchingMore: boolean
  isInfiniteMode: boolean
  isVirtualizationEnabled: boolean
  loadingMoreMessage: ReactNode
  measureRowRef?: (element: HTMLTableRowElement | null) => void
  /** Ours: the height each row holds under the column window, by item key. */
  rowHeights?: Map<VirtualItem['key'], number>
  table: DataGridTableInstance<TData>
  topRows: Row<DataGridFeatures, TData>[]
  totalSize: number
  virtualItems: VirtualItem[]
}

function DataGridTableVirtualPinnedPlaceholderCell<TData extends object>({
  column,
}: {
  column: Column<DataGridFeatures, TData, unknown>
}) {
  const { props, table } = useDataGrid<TData>()
  // Ours: sticky, not merely pinned (see getDataGridStickySide).
  const isPinned = getDataGridStickySide(column, props.stickySides)
  const stickyEdge = getDataGridStickyEdge(table, column, props.stickySides)

  return (
    <td
      aria-hidden="true"
      className={cn(
        'p-0',
        props.tableLayout?.cellBorder && 'border-e',
        getDataGridCanStick(column, props) &&
          'data-pinned:isolate data-pinned:bg-[color:var(--data-grid-surface,var(--data-grid-card-surface,var(--background)))] [&[data-pinned=end][data-last-col=end]]:shadow-[inset_calc(1px*var(--data-grid-dir,1))_0_0_0_var(--border)] [&[data-pinned=start][data-last-col=start]]:shadow-[inset_calc(-1px*var(--data-grid-dir,1))_0_0_0_var(--border)]',
      )}
      data-last-col={stickyEdge}
      data-pinned={isPinned || undefined}
      style={{
        ...(getDataGridCanStick(column, props) && getPinningStyles(column, props)),
        ...(props.tableLayout?.columnsResizable && {
          width: `calc(var(--col-${column.id}-size) * 1px)`,
        }),
      }}
    data-sentry-component="DataGridTableVirtualPinnedPlaceholderCell" data-sentry-source-file="data-grid-table-virtual.tsx" />
  )
}

function DataGridTableVirtualSpacer<TData extends object>({
  height,
  table,
}: {
  height: number
  table: DataGridTableInstance<TData>
}) {
  if (height <= 0) return null

  return (
    <DataGridTableVirtualUtilityRow
      ariaHidden
      centerCellClassName="p-0"
      centerCellStyle={{ height, padding: 0 }}
      table={table}
    data-sentry-element="DataGridTableVirtualUtilityRow" data-sentry-component="DataGridTableVirtualSpacer" data-sentry-source-file="data-grid-table-virtual.tsx">
      {null}
    </DataGridTableVirtualUtilityRow>
  )
}

function DataGridTableVirtualStatusRow<TData extends object>({
  children,
  className,
  table,
}: {
  children: ReactNode
  className?: string
  table: DataGridTableInstance<TData>
}) {
  return (
    <DataGridTableVirtualUtilityRow
      centerCellClassName={cn('py-4 text-center text-sm text-muted-foreground', className)}
      table={table}
    data-sentry-element="DataGridTableVirtualUtilityRow" data-sentry-component="DataGridTableVirtualStatusRow" data-sentry-source-file="data-grid-table-virtual.tsx">
      {children}
    </DataGridTableVirtualUtilityRow>
  )
}

function DataGridTableVirtualUtilityRow<TData extends object>({
  ariaHidden,
  centerCellClassName,
  centerCellStyle,
  children,
  rowClassName,
  table,
}: {
  ariaHidden?: boolean
  centerCellClassName?: string
  centerCellStyle?: CSSProperties
  children: ReactNode
  rowClassName?: string
  table: DataGridTableInstance<TData>
}) {
  const { props } = useDataGrid()
  const leftVisibleColumns = table.getStartVisibleLeafColumns()
  const centerVisibleColumns = table.getCenterVisibleLeafColumns()
  const rightVisibleColumns = table.getEndVisibleLeafColumns()
  const hasRightPinnedColumns = hasDataGridTableRightPinnedColumns(table)

  return (
    <tr aria-hidden={ariaHidden || undefined} className={rowClassName} data-sentry-component="DataGridTableVirtualUtilityRow" data-sentry-source-file="data-grid-table-virtual.tsx">
      {leftVisibleColumns.map(column => (
        <DataGridTableVirtualPinnedPlaceholderCell column={column} key={column.id} />
      ))}
      <td
        className={centerCellClassName}
        colSpan={Math.max(centerVisibleColumns.length, 1)}
        style={centerCellStyle}
      >
        {children}
      </td>
      {props.tableLayout?.columnsResizable && hasRightPinnedColumns ? (
        <DataGridTableFillBodyCell />
      ) : null}
      {rightVisibleColumns.map(column => (
        <DataGridTableVirtualPinnedPlaceholderCell column={column} key={column.id} />
      ))}
      {props.tableLayout?.columnsResizable && !hasRightPinnedColumns ? (
        <DataGridTableFillBodyCell />
      ) : null}
    </tr>
  )
}

function getDataGridTableHeaderOffset({
  containerElement,
  headerSticky,
  scrollElement,
}: {
  containerElement: HTMLDivElement
  headerSticky: boolean
  scrollElement: HTMLElement
}) {
  if (!headerSticky) return 0

  const headerElement = containerElement.querySelector<HTMLElement>(
    ':scope > [data-slot="data-grid-table"] > thead',
  )

  if (!headerElement) return 0

  const scrollRect = scrollElement.getBoundingClientRect()
  const headerRect = headerElement.getBoundingClientRect()
  const headerBottomOffset = headerRect.bottom - scrollRect.top
  const overlapsViewportTop = headerRect.top <= scrollRect.top + 0.5 && headerBottomOffset > 0

  if (!overlapsViewportTop) return 0

  return Math.min(scrollElement.clientHeight, Math.max(0, headerBottomOffset))
}

function getDataGridTableScrollTarget({
  align,
  clientHeight,
  rowBottom,
  rowHeight,
  rowTop,
  scrollHeight,
  scrollTop,
  viewportTopOffset = 0,
}: {
  align: DataGridTableVirtualScrollAlignment
  clientHeight: number
  rowBottom: number
  rowHeight: number
  rowTop: number
  scrollHeight: number
  scrollTop: number
  viewportTopOffset?: number
}) {
  const visibleHeight = Math.max(0, clientHeight - viewportTopOffset)
  const viewportTop = scrollTop + viewportTopOffset
  const viewportBottom = scrollTop + clientHeight

  const targetTop =
    align === 'auto'
      ? rowTop < viewportTop
        ? rowTop - viewportTopOffset
        : rowBottom > viewportBottom
          ? rowBottom - clientHeight
          : null
      : align === 'start'
        ? rowTop - viewportTopOffset
        : align === 'end'
          ? rowBottom - clientHeight
          : rowTop - viewportTopOffset - Math.max(0, (visibleHeight - rowHeight) / 2)

  if (targetTop === null) return null

  return Math.min(Math.max(0, targetTop), Math.max(0, scrollHeight - clientHeight))
}

function isSameDataGridTableColumnScrollRequest(
  previous: DataGridTableColumnScrollRequest | null,
  next: DataGridTableColumnScrollRequest,
) {
  return (
    previous?.align === next.align &&
    previous.behavior === next.behavior &&
    previous.columnId === next.columnId &&
    previous.columnIndex === next.columnIndex &&
    previous.scrollElement === next.scrollElement
  )
}

function isSameDataGridTableScrollRequest(
  previous: DataGridTableVirtualScrollRequest | null,
  next: DataGridTableVirtualScrollRequest,
) {
  return (
    previous?.align === next.align &&
    previous.behavior === next.behavior &&
    previous.containerElement === next.containerElement &&
    previous.headerSticky === next.headerSticky &&
    previous.isVirtualizationEnabled === next.isVirtualizationEnabled &&
    previous.rowId === next.rowId &&
    previous.rowIndex === next.rowIndex &&
    previous.scrollElement === next.scrollElement
  )
}

function scrollDataGridTableRowIntoView({
  align,
  behavior,
  cancelPendingScroll = false,
  containerElement,
  headerSticky,
  rowIndex,
  scrollElement,
  virtualizer,
}: {
  align: DataGridTableVirtualScrollAlignment
  behavior: ScrollBehavior
  cancelPendingScroll?: boolean
  containerElement: HTMLDivElement | null
  headerSticky: boolean
  rowIndex: number
  scrollElement: HTMLElement | null
  virtualizer?: DataGridTableVirtualizerInstance
}) {
  if (!containerElement || !scrollElement) return false

  const rowElement = containerElement.querySelector<HTMLTableRowElement>(
    `:scope > [data-slot="data-grid-table"] > tbody > tr[data-index="${rowIndex}"]`,
  )

  if (!rowElement) return false

  const scrollRect = scrollElement.getBoundingClientRect()
  const rowRect = rowElement.getBoundingClientRect()
  const viewportTopOffset = getDataGridTableHeaderOffset({
    containerElement,
    headerSticky,
    scrollElement,
  })
  const rowTop = scrollElement.scrollTop + rowRect.top - scrollRect.top
  const rowBottom = scrollElement.scrollTop + rowRect.bottom - scrollRect.top
  const targetTop = getDataGridTableScrollTarget({
    align,
    clientHeight: scrollElement.clientHeight,
    rowBottom,
    rowHeight: rowRect.height || rowElement.offsetHeight,
    rowTop,
    scrollHeight: scrollElement.scrollHeight,
    scrollTop: scrollElement.scrollTop,
    viewportTopOffset,
  })

  if (targetTop === null || Math.abs(targetTop - scrollElement.scrollTop) < 0.5) {
    if (cancelPendingScroll) {
      scrollDataGridTableToOffset({
        behavior: 'auto',
        scrollElement,
        targetTop: scrollElement.scrollTop,
        virtualizer,
      })
    }

    return true
  }

  scrollDataGridTableToOffset({
    behavior,
    scrollElement,
    targetTop,
    virtualizer,
  })

  return true
}

function scrollDataGridTableToOffset({
  behavior,
  scrollElement,
  targetTop,
  virtualizer,
}: {
  behavior: ScrollBehavior
  scrollElement: HTMLElement
  targetTop: number
  virtualizer?: DataGridTableVirtualizerInstance
}) {
  if (virtualizer) {
    virtualizer.scrollToOffset(targetTop, { align: 'start', behavior })
  } else if (typeof scrollElement.scrollTo === 'function') {
    scrollElement.scrollTo({ behavior, top: targetTop })
  } else {
    scrollElement.scrollTop = targetTop
  }
}

/**
 * A scroll frame only shifts the window, so every surviving row's inputs are
 * identical and its last render is reused; the per-frame cost is the rows
 * entering the window, not all mounted rows. Cell-level state (selection,
 * focus, row checks) repaints through each cell's own Subscribe, and any real
 * data change rebuilds the row wrappers, so identity comparison is safe.
 */
const MemoizedRenderedRow = memo(
  DataGridTableRenderedRow,
  (prev, next) =>
    prev.row === next.row &&
    prev.rowIndex === next.rowIndex &&
    prev.rowRef === next.rowRef &&
    // Ours: the height held under the column window.
    prev.rowHeight === next.rowHeight &&
    prev.pinnedBoundary === next.pinnedBoundary &&
    prev.centerWindow?.start === next.centerWindow?.start &&
    prev.centerWindow?.end === next.centerWindow?.end,
) as typeof DataGridTableRenderedRow

function DataGridTableVirtualBody<TData extends object>({
  allRowsLoadedMessage,
  bottomRows,
  centerColumnWindow,
  centerRows,
  hasMore,
  isFetchingMore,
  isInfiniteMode,
  isVirtualizationEnabled,
  loadingMoreMessage,
  measureRowRef,
  rowHeights,
  table,
  topRows,
  totalSize,
  virtualItems,
}: VirtualBodyProps<TData>) {
  const { isLoading } = useDataGrid()
  const totalRows = topRows.length + centerRows.length + bottomRows.length

  if (!totalRows) {
    // Initial load must not flash the empty state as if the query returned
    // nothing.
    if (isLoading) {
      return (
        <DataGridTableVirtualStatusRow table={table}>
          <div className="flex items-center justify-center gap-2">
            <Spinner className="size-4 opacity-60" />
            {loadingMoreMessage}
          </div>
        </DataGridTableVirtualStatusRow>
      )
    }

    return <DataGridTableEmpty />
  }

  const hasCenterRows = centerRows.length > 0
  const showFetchingRow = isInfiniteMode && isFetchingMore
  const showCompleteRow = isInfiniteMode && hasMore === false && totalRows > 0
  const hasMiddleSection = hasCenterRows || showFetchingRow || showCompleteRow
  const leadingSpacerHeight =
    isVirtualizationEnabled && hasCenterRows && virtualItems.length > 0
      ? (virtualItems[0]?.start ?? 0)
      : 0
  const trailingSpacerHeight =
    isVirtualizationEnabled && hasCenterRows && virtualItems.length > 0
      ? Math.max(0, totalSize - (virtualItems[virtualItems.length - 1]?.end ?? 0))
      : 0

  const renderedRows: ReactNode[] = []

  topRows.forEach((row, index) => {
    renderedRows.push(
      <MemoizedRenderedRow
        centerWindow={centerColumnWindow}
        key={row.id}
        pinnedBoundary={index === topRows.length - 1 && hasMiddleSection ? 'top' : undefined}
        row={row}
      />,
    )
  })

  if (isVirtualizationEnabled) {
    if (leadingSpacerHeight > 0) {
      renderedRows.push(
        <DataGridTableVirtualSpacer
          height={leadingSpacerHeight}
          key="virtual-spacer-start"
          table={table}
        />,
      )
    }

    virtualItems.forEach(virtualRow => {
      const row = centerRows[virtualRow.index]

      if (!row) return

      renderedRows.push(
        <MemoizedRenderedRow
          centerWindow={centerColumnWindow}
          key={row.id}
          row={row}
          rowHeight={rowHeights?.get(virtualRow.key)}
          rowIndex={virtualRow.index}
          rowRef={measureRowRef}
        />,
      )
    })

    if (trailingSpacerHeight > 0) {
      renderedRows.push(
        <DataGridTableVirtualSpacer
          height={trailingSpacerHeight}
          key="virtual-spacer-end"
          table={table}
        />,
      )
    }
  } else {
    centerRows.forEach((row, rowIndex) => {
      renderedRows.push(
        <MemoizedRenderedRow
          centerWindow={centerColumnWindow}
          key={row.id}
          row={row}
          rowIndex={rowIndex}
        />,
      )
    })
  }

  if (showFetchingRow) {
    renderedRows.push(
      <DataGridTableVirtualStatusRow key="virtual-status-loading" table={table}>
        <div className="flex items-center justify-center gap-2">
          <Spinner className="size-4 opacity-60" />
          {loadingMoreMessage}
        </div>
      </DataGridTableVirtualStatusRow>,
    )
  }

  if (showCompleteRow) {
    renderedRows.push(
      <DataGridTableVirtualStatusRow
        className="py-3 text-xs"
        key="virtual-status-complete"
        table={table}
      >
        {allRowsLoadedMessage}
      </DataGridTableVirtualStatusRow>,
    )
  }

  bottomRows.forEach((row, index) => {
    renderedRows.push(
      <MemoizedRenderedRow
        centerWindow={centerColumnWindow}
        key={row.id}
        pinnedBoundary={
          index === 0 && (topRows.length > 0 || hasMiddleSection) ? 'bottom' : undefined
        }
        row={row}
      />,
    )
  })

  return <>{renderedRows}</>
}

/**
 * Memoized virtual body: skip re-renders during active column resize.
 * Column widths update via CSS variables on the <table> element,
 * so the browser handles width changes without React re-renders.
 * A cell-selection drag gets the same treatment: painting goes through each
 * cell's own Subscribe, and the virtualizer re-renders itself from inside the
 * memo boundary, so parent-driven reconciliation during the drag is waste.
 */
const MemoizedVirtualBody = memo(
  DataGridTableVirtualBody,
  (_prev, next) =>
    !!next.table.state.columnResizing?.isResizingColumn || next.table._isSelectingCells === true,
) as typeof DataGridTableVirtualBody

function DataGridTableVirtual<TData extends object>({
  columnVirtualizerOptions,
  estimateSize = 48,
  fetchMoreOffset = 0,
  footerContent,
  hasMore,
  header,
  height,
  isFetchingMore = false,
  onFetchMore,
  overscan = 10,
  renderHeader = true,
  rows,
  scrollBehavior = 'auto',
  scrollElementRef,
  scrollToColumnAlign = 'auto',
  scrollToColumnIndex,
  scrollToRowAlign = 'auto',
  scrollToRowIndex,
  tableRef,
  virtualizerOptions,
}: DataGridTableVirtualProps<TData>) {
  const { i18n, props, table } = useDataGrid<TData>()
  const columnSizing = useDataGridAtom(table.atoms.columnSizing)
  const mergedHeaderGroups = getDataGridTableMergedHeaderGroups(table)
  const hasRightPinnedColumns = hasDataGridTableRightPinnedColumns(table)
  const centerVisibleColumns = table.getCenterVisibleLeafColumns()
  // Column windows only where the geometry is provable: a fixed table
  // layout (undefined defaults to fixed; the colgroup then owns every
  // width, so colSpan spacers cannot drift) and a single ungrouped header
  // row (a colSpan group or meta.headerColSpan cell cannot be windowed).
  // Anything else falls back to full-column rendering silently, the same
  // posture as row virtualization toward unsupported shapes.
  const columnVirtualizationActive =
    columnVirtualizerOptions?.enabled === true &&
    props.tableLayout?.width !== 'auto' &&
    mergedHeaderGroups.length === 1 &&
    (mergedHeaderGroups[0]?.headers.every(header => header.colSpan <= 1) ?? false) &&
    !mergedHeaderGroups[0]?.coveredHeaders.size
  const { bottomRows, centerRows, topRows } = getDataGridTableRowSections(
    table,
    props.tableLayout?.rowsPinnable,
  )
  const isInfiniteMode = typeof onFetchMore === 'function'
  const [viewportElements, setViewportElements] = useState<DataGridTableVirtualScrollElements>({
    containerElement: null,
    scrollElement: null,
  })

  const {
    estimateSize: customEstimateSize,
    getItemKey: customGetItemKey,
    getScrollElement: customGetScrollElement,
    measureElement: customMeasureElement,
    overscan: customOverscan,
    ...virtualizerOptionsRest
  } = virtualizerOptions ?? {}

  const isVirtualizationEnabled = virtualizerOptions?.enabled !== false
  const loadingMoreMessage =
    props.fetchingMoreMessage || props.loadingMessage || i18n.labels.loading
  const allRowsLoadedMessage = props.allRowsLoadedMessage || i18n.labels.allRowsLoaded

  const handleViewportRef = useCallback((node: HTMLDivElement | null) => {
    setViewportElements({
      containerElement: node,
      scrollElement: node ? (getDataGridScrollAreaViewport(node) ?? node) : null,
    })
  }, [])

  const usesExternalScrollArea =
    // Ours: an ancestor's scroll element.
    !!scrollElementRef ||
    (viewportElements.scrollElement !== null &&
      viewportElements.scrollElement !== viewportElements.containerElement)

  const resolveScrollElement = useCallback(() => {
    // Ours: an ancestor's scroll element.
    if (scrollElementRef) return scrollElementRef.current
    if (customGetScrollElement) {
      return customGetScrollElement(viewportElements)
    }

    return viewportElements.scrollElement
  }, [customGetScrollElement, scrollElementRef, viewportElements])

  const resolveItemKey = useCallback(
    (index: number) => {
      const row = centerRows[index]

      if (!row) return index

      return customGetItemKey?.(index, row) ?? row.id ?? index
    },
    [centerRows, customGetItemKey],
  )

  const resolveEstimateSize = useCallback(
    (index: number) => {
      const row = centerRows[index]

      return row ? (customEstimateSize?.(index, row) ?? estimateSize) : estimateSize
    },
    [centerRows, customEstimateSize, estimateSize],
  )

  // Ours: a column resize drag reflows the rows on every move, and measuring them then re-renders
  // the virtual body, mounting and dropping rows mid-drag. They keep their heights until the drag
  // ends and are measured once after it (below).
  const isResizingColumn = () => !!table.atoms.columnResizing?.get()?.isResizingColumn
  // Ours: under the column window a row shows only some of its cells, so its height follows the
  // tallest one in view, and scrolling sideways would grow and shrink it, moving every row below.
  // Each row instead holds the tallest height it has shown (`rowHeight`), until the rows or the
  // column layout change.
  const rowHeights = useMemo(
    () => new Map<VirtualItem['key'], number>(),
    // The map is a cache of what rows have shown, so a new layout starts an empty one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      centerRows,
      columnSizing,
      estimateSize,
      table.state.columnOrder,
      table.state.columnPinning,
      table.state.columnVisibility,
    ],
  )
  const measureElement: typeof customMeasureElement =
    customMeasureElement &&
    ((element, entry, instance) => {
      const index = instance.indexFromElement(element)
      const kept = isResizingColumn() ? instance.measurementsCache[index]?.size : undefined
      if (kept !== undefined) return kept
      const size = customMeasureElement(element, entry, instance)
      if (columnVirtualizationActive) {
        const key = instance.options.getItemKey(index)
        rowHeights.set(key, Math.max(size, rowHeights.get(key) ?? 0))
      }
      return size
    })

  const virtualizer = useVirtualizer({
    count: centerRows.length,
    estimateSize: resolveEstimateSize,
    getItemKey: resolveItemKey,
    getScrollElement: resolveScrollElement,
    measureElement,
    overscan: customOverscan ?? overscan,
    ...virtualizerOptionsRest,
  }) as DataGridTableVirtualizerInstance

  useEffect(() => {
    const resizing = table.atoms.columnResizing
    if (!resizing || !customMeasureElement) return
    let wasResizing = isResizingColumn()
    let frame: null | number = null
    const subscription = resizing.subscribe(() => {
      const isResizing = isResizingColumn()
      if (wasResizing && !isResizing) {
        // After the release commit has laid the rows out at their new widths.
        frame = requestAnimationFrame(() => {
          for (const element of virtualizer.elementsCache.values()) {
            virtualizer.measureElement(element)
          }
        })
      }
      wasResizing = isResizing
    })
    return () => {
      subscription.unsubscribe()
      if (frame !== null) cancelAnimationFrame(frame)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.atoms.columnResizing, virtualizer, !customMeasureElement])

  // Horizontal offsets invert in RTL; the virtualizer must be told.
  const isRtl = useMemo(
    () =>
      viewportElements.containerElement
        ? getComputedStyle(viewportElements.containerElement).direction === 'rtl'
        : false,
    [viewportElements.containerElement],
  )

  const resolveColumnKey = useCallback(
    (index: number) => centerVisibleColumns[index]?.id ?? index,
    [centerVisibleColumns],
  )

  const resolveColumnEstimateSize = useCallback(
    (index: number) => centerVisibleColumns[index]?.getSize() ?? 0,
    [centerVisibleColumns],
  )

  const columnVirtualizer = useVirtualizer({
    count: columnVirtualizationActive ? centerVisibleColumns.length : 0,
    estimateSize: resolveColumnEstimateSize,
    getItemKey: resolveColumnKey,
    getScrollElement: resolveScrollElement,
    horizontal: true,
    isRtl,
    observeElementOffset: observeColumnOffset,
    overscan: columnVirtualizerOptions?.overscan ?? 3,
  })

  // Column sizes are exact reads of getSize(), never DOM-measured, so a
  // resize commit or a visibility/order/pinning change must resync the
  // virtualizer's cached sizes by hand.
  useEffect(() => {
    if (columnVirtualizationActive) columnVirtualizer.measure()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columnVirtualizationActive,
    columnVirtualizer,
    columnSizing,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    table.state.columnVisibility,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    table.state.columnOrder,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    table.state.columnPinning,
  ])

  const virtualColumns = columnVirtualizationActive ? columnVirtualizer.getVirtualItems() : []
  // The window is a contiguous inclusive [start, end] over the center
  // columns; before the scroll element resolves the item list is empty and
  // the grid renders full-width for that first frame.
  const centerColumnWindow =
    columnVirtualizationActive && virtualColumns.length > 0
      ? {
          end: virtualColumns[virtualColumns.length - 1]!.index,
          start: virtualColumns[0]!.index,
        }
      : undefined

  const virtualItems = isVirtualizationEnabled ? virtualizer.getVirtualItems() : []
  const totalSize = isVirtualizationEnabled ? virtualizer.getTotalSize() : 0
  const measureRowRef =
    isVirtualizationEnabled && customMeasureElement ? virtualizer.measureElement : undefined
  const resolvedFetchMoreOffset = Math.max(0, fetchMoreOffset)
  const scrollToRowId =
    scrollToRowIndex !== undefined ? centerRows[scrollToRowIndex]?.id : undefined
  const scrollToRowVirtualItem =
    isVirtualizationEnabled && scrollToRowIndex !== undefined
      ? virtualItems.find(item => item.index === scrollToRowIndex)
      : undefined
  const pendingScrollToRowIndexRef = useRef<null | number>(null)
  const lastScrollRequestRef = useRef<DataGridTableVirtualScrollRequest | null>(null)
  // Latch onFetchMore per row count: virtualItems gets a new identity every
  // scroll frame, so without it the effect fires duplicate page requests
  // before the consumer flips isFetchingMore, and loops at end-of-data when
  // hasMore is never set.
  const fetchMoreFiredAtCountRef = useRef<null | number>(null)
  // Ours: a result replaced by one of the same size (a re-sort or a new filter) shows as a new first
  // or last row, and re-arms the latch. A failed fetch or a page that adds no rows changes neither,
  // so neither fetches again.
  const firstRowId = centerRows[0]?.id
  const lastRowId = centerRows.at(-1)?.id
  const fetchMoreBoundaryRef = useRef({ first: firstRowId, last: lastRowId })
  const lastColumnScrollRequestRef = useRef<DataGridTableColumnScrollRequest | null>(null)

  // Controlled column reveal: same dedupe posture as the row request - the
  // signature keeps ordinary renders from re-scrolling, and exact column
  // sizes mean no post-measure follow-up pass is needed.
  useEffect(() => {
    if (
      !columnVirtualizationActive ||
      scrollToColumnIndex === undefined ||
      scrollToColumnIndex < 0 ||
      scrollToColumnIndex >= centerVisibleColumns.length ||
      !viewportElements.scrollElement
    ) {
      return
    }
    const request: DataGridTableColumnScrollRequest = {
      align: scrollToColumnAlign,
      behavior: scrollBehavior,
      columnId: centerVisibleColumns[scrollToColumnIndex]?.id,
      columnIndex: scrollToColumnIndex,
      scrollElement: viewportElements.scrollElement,
    }
    if (isSameDataGridTableColumnScrollRequest(lastColumnScrollRequestRef.current, request)) {
      return
    }
    lastColumnScrollRequestRef.current = request
    columnVirtualizer.scrollToIndex(scrollToColumnIndex, {
      align: scrollToColumnAlign,
      behavior: scrollBehavior === 'smooth' ? 'smooth' : 'auto',
    })
  }, [
    columnVirtualizationActive,
    columnVirtualizer,
    centerVisibleColumns,
    scrollToColumnAlign,
    scrollToColumnIndex,
    scrollBehavior,
    viewportElements.scrollElement,
  ])

  // Resolve after every commit so a stable getter can expose a replaced ref;
  // the request signature prevents duplicate scrolling on ordinary renders.
  useEffect(() => {
    const previousRequest = lastScrollRequestRef.current

    if (
      scrollToRowIndex === undefined ||
      scrollToRowIndex < 0 ||
      scrollToRowIndex >= centerRows.length
    ) {
      pendingScrollToRowIndexRef.current = null
      lastScrollRequestRef.current = null

      if (previousRequest) {
        const scrollElement = resolveScrollElement()

        if (scrollElement) {
          scrollDataGridTableToOffset({
            behavior: 'auto',
            scrollElement,
            targetTop: scrollElement.scrollTop,
            virtualizer: isVirtualizationEnabled ? virtualizer : undefined,
          })
        }
      }

      return
    }

    const scrollElement = resolveScrollElement()
    const containerElement = viewportElements.containerElement
    if (!containerElement || !scrollElement) return

    const headerSticky = renderHeader && !!props.tableLayout?.headerSticky
    const nextRequest: DataGridTableVirtualScrollRequest = {
      align: scrollToRowAlign,
      behavior: scrollBehavior,
      containerElement,
      headerSticky,
      isVirtualizationEnabled,
      rowId: scrollToRowId,
      rowIndex: scrollToRowIndex,
      scrollElement,
    }

    if (isSameDataGridTableScrollRequest(previousRequest, nextRequest)) return

    pendingScrollToRowIndexRef.current = null

    const rowWasHandled = scrollDataGridTableRowIntoView({
      align: scrollToRowAlign,
      behavior: scrollBehavior,
      cancelPendingScroll: previousRequest !== null,
      containerElement,
      headerSticky,
      rowIndex: scrollToRowIndex,
      scrollElement,
      virtualizer: isVirtualizationEnabled ? virtualizer : undefined,
    })

    if (rowWasHandled) {
      lastScrollRequestRef.current = nextRequest
      return
    }

    if (!isVirtualizationEnabled) return

    pendingScrollToRowIndexRef.current = scrollToRowIndex
    lastScrollRequestRef.current = nextRequest
    virtualizer.scrollToIndex(scrollToRowIndex, {
      align: scrollToRowAlign,
      behavior: scrollBehavior,
    })
  })

  useEffect(() => {
    if (
      !isVirtualizationEnabled ||
      scrollToRowIndex === undefined ||
      pendingScrollToRowIndexRef.current !== scrollToRowIndex ||
      !scrollToRowVirtualItem
    ) {
      return
    }

    const rowWasHandled = scrollDataGridTableRowIntoView({
      align: scrollToRowAlign,
      behavior: 'auto',
      cancelPendingScroll: true,
      containerElement: viewportElements.containerElement,
      headerSticky: renderHeader && !!props.tableLayout?.headerSticky,
      rowIndex: scrollToRowIndex,
      scrollElement: resolveScrollElement(),
      virtualizer,
    })

    if (rowWasHandled) {
      pendingScrollToRowIndexRef.current = null
    }
  }, [
    isVirtualizationEnabled,
    props.tableLayout?.headerSticky,
    renderHeader,
    resolveScrollElement,
    scrollToRowAlign,
    scrollToRowIndex,
    scrollToRowVirtualItem,
    virtualizer,
    viewportElements.containerElement,
  ])

  // Ours: whether a consumer's `rows` replace the body, as skeleton rows do while a re-sort loads.
  const isBodyReplaced = rows !== undefined

  useEffect(() => {
    const boundary = fetchMoreBoundaryRef.current
    // Ours: replaced rows re-arm the latch, as a remounted body would, so a re-sort that returns the
    // same first row, last row and count still fetches more.
    if (isBodyReplaced || boundary.first !== firstRowId || boundary.last !== lastRowId) {
      fetchMoreBoundaryRef.current = { first: firstRowId, last: lastRowId }
      fetchMoreFiredAtCountRef.current = null
    }
    if (isBodyReplaced) return

    if (!isVirtualizationEnabled || !isInfiniteMode || hasMore === false || isFetchingMore) {
      return
    }

    const lastItem = virtualItems[virtualItems.length - 1]
    if (!lastItem) return

    if (fetchMoreFiredAtCountRef.current === centerRows.length) return

    if (lastItem.index >= centerRows.length - 1 - resolvedFetchMoreOffset) {
      fetchMoreFiredAtCountRef.current = centerRows.length
      onFetchMore?.()
    }
  }, [
    centerRows.length,
    firstRowId,
    hasMore,
    isBodyReplaced,
    isFetchingMore,
    isInfiniteMode,
    isVirtualizationEnabled,
    lastRowId,
    onFetchMore,
    resolvedFetchMoreOffset,
    virtualItems,
  ])

  // The header re-renders only when its real inputs move: the table
  // wrapper (any table state change recreates it), the layout props, and
  // the column window. A scroll frame changes none of them, so the whole
  // sortable-header subtree is reused instead of rebuilt per frame.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const headerNode = useMemo(
    () =>
      renderHeader &&
      // Ours: a consumer's `header` replaces this one, so it isn't built.
      header == null && (
        <DataGridTableHead>
          {mergedHeaderGroups.map((headerGroup, rowIndex) => {
            // The fill strip is one cell spanning every head row, so only
            // the first row renders it.
            const fillHeadCell =
              props.tableLayout?.columnsResizable && rowIndex === 0 ? (
                <DataGridTableFillHeadCell rowSpan={mergedHeaderGroups.length} />
              ) : null
            const renderHeadCell = (header: (typeof headerGroup.startHeaders)[number]) => {
              const coveredHeaders = headerGroup.coveredHeaders.get(header.id)
              const resizeHeader = getDataGridHeadCellResizeHeader(header, coveredHeaders)

              return (
                <DataGridTableHeadRowCell
                  coveredHeaders={coveredHeaders}
                  header={header}
                  key={header.id}
                data-sentry-element="DataGridTableHeadRowCell" data-sentry-component="renderHeadCell" data-sentry-source-file="data-grid-table-virtual.tsx">
                  {renderDataGridTableHeadCellContent(header, props)}
                  {props.tableLayout?.columnsResizable && resizeHeader.column.getCanResize() && (
                    <DataGridTableHeadRowCellResize header={resizeHeader} />
                  )}
                </DataGridTableHeadRowCell>
              )
            }

            return (
              <DataGridTableHeadRow key={headerGroup.id} rowId={headerGroup.id}>
                {/* Under an active column window the single ungrouped header
                        row is bucketed start / windowed center / end, with each
                        off-window flank one colSpan spacer sized by the intact
                        colgroup - the same shape the body rows take. */}
                {centerColumnWindow ? headerGroup.startHeaders.map(renderHeadCell) : null}
                {centerColumnWindow && centerColumnWindow.start > 0 ? (
                  <th
                    aria-hidden="true"
                    className="p-0"
                    colSpan={centerColumnWindow.start}
                    data-col-spacer="start"
                  />
                ) : null}
                {centerColumnWindow
                  ? headerGroup.centerHeaders
                      .slice(centerColumnWindow.start, centerColumnWindow.end + 1)
                      .map(renderHeadCell)
                  : null}
                {centerColumnWindow &&
                centerVisibleColumns.length - 1 - centerColumnWindow.end > 0 ? (
                  <th
                    aria-hidden="true"
                    className="p-0"
                    colSpan={centerVisibleColumns.length - 1 - centerColumnWindow.end}
                    data-col-spacer="end"
                  />
                ) : null}
                {!centerColumnWindow &&
                  [...headerGroup.startHeaders, ...headerGroup.centerHeaders].map(renderHeadCell)}
                {hasRightPinnedColumns ? fillHeadCell : null}
                {headerGroup.endHeaders.map(renderHeadCell)}
                {!hasRightPinnedColumns ? fillHeadCell : null}
              </DataGridTableHeadRow>
            )
          })}
        </DataGridTableHead>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [renderHeader, header, table, props.tableLayout, centerColumnWindow, hasRightPinnedColumns],
  )

  return (
    <DataGridTableViewport
      className={!usesExternalScrollArea ? 'block' : undefined}
      style={
        usesExternalScrollArea
          ? undefined
          : {
              height,
              overflow: 'auto',
              position: 'relative',
              // Standalone mode: this node IS the scroll container, so it
              // must stay at its parent's width (not the resizable table
              // width) or horizontal scrolling becomes impossible.
              width: 'auto',
            }
      }
      viewportRef={handleViewportRef}
    data-sentry-element="DataGridTableViewport" data-sentry-component="DataGridTableVirtual" data-sentry-source-file="data-grid-table-virtual.tsx">
      {/* Ours: `tableRef`. */}
      <DataGridTableBase tableRef={tableRef} data-sentry-element="DataGridTableBase" data-sentry-source-file="data-grid-table-virtual.tsx">
        {/* Ours: a consumer's `header` replaces the built-in head. */}
        {header ?? headerNode}

        {renderHeader &&
          // Ours: no spacer after a consumer's `header`.
          !header &&
          (props.tableLayout?.stripped || !props.tableLayout?.rowBorder) && (
            <DataGridTableRowSpacer />
          )}

        <DataGridTableBody data-sentry-element="DataGridTableBody" data-sentry-source-file="data-grid-table-virtual.tsx">
          {/* Ours: a consumer's `rows` replace the virtual body. */}
          {rows ?? (
            <MemoizedVirtualBody
              allRowsLoadedMessage={allRowsLoadedMessage}
              bottomRows={bottomRows}
              centerColumnWindow={centerColumnWindow}
              centerRows={centerRows}
              hasMore={hasMore}
              isFetchingMore={isFetchingMore}
              isInfiniteMode={isInfiniteMode}
              isVirtualizationEnabled={isVirtualizationEnabled}
              loadingMoreMessage={loadingMoreMessage}
              measureRowRef={measureRowRef}
              rowHeights={columnVirtualizationActive ? rowHeights : undefined}
              table={table}
              topRows={topRows}
              totalSize={totalSize}
              virtualItems={virtualItems}
            />
          )}
          {/* Same appended region as the standard body, so onRowCreate and
              a consumer draft work in the virtual layout too. */}
          {props.appendRow}
          <DataGridTableAddRow data-sentry-element="DataGridTableAddRow" data-sentry-source-file="data-grid-table-virtual.tsx" />
        </DataGridTableBody>

        {footerContent && <DataGridTableFoot>{footerContent}</DataGridTableFoot>}
      </DataGridTableBase>
    </DataGridTableViewport>
  )
}

export { DataGridTableVirtual }
export type {
  DataGridTableVirtualizerOptions,
  DataGridTableVirtualProps,
  DataGridTableVirtualScrollAlignment,
  DataGridTableVirtualScrollElements,
}
